import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox,
  Calendar,
  UserPlus,
  ShoppingCart,
  Users,
  Wrench,
  ArrowUpRight,
  Loader2,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

function formatDateHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return "—";
  }
}

const METRICS = [
  {
    key: "pending_envelopes",
    title: "Ellenőrzésre váró borítékok",
    icon: Inbox,
    accent: "indigo",
    href: "/daily-revenues",
    cta: "Borítékok megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={`${it.site_name || "—"}`}
        secondary={`${formatDateHu(it.date)} · ${(it.total || 0).toLocaleString("hu-HU")} Ft`}
        meta={it.created_by_name}
      />
    ),
  },
  {
    key: "scheduled_today",
    title: "Mai napra beosztottak",
    icon: Calendar,
    accent: "emerald",
    href: "/schedule",
    cta: "Beosztás megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={it.employee_name}
        secondary={`${it.template_name} · ${it.start_time}–${it.end_time}`}
        meta={it.site_name}
      />
    ),
  },
  {
    key: "onboarding",
    title: "Onboarding alatt lévők",
    icon: UserPlus,
    accent: "amber",
    href: "/employees",
    cta: "Munkavállalók megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={it.name}
        secondary={it.position}
        meta={formatDateHu(it.created_at)}
      />
    ),
  },
  {
    key: "in_progress_orders",
    title: "Folyamatban lévő beszállítói rendelések",
    icon: ShoppingCart,
    accent: "violet",
    href: "/supplier-orders",
    cta: "Rendelések megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={it.order_number || it.supplier_name || "Rendelés"}
        secondary={it.supplier_name}
        meta={it.status === "draft" ? "Vázlat" : "Elküldve"}
      />
    ),
  },
  {
    key: "new_applicants",
    title: "Új jelöltek bármely toborzáson",
    icon: Users,
    accent: "rose",
    href: "/recruitment",
    cta: "Toborzás megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={`${it.last_name || ""} ${it.first_name || ""}`.trim() || it.email || "—"}
        secondary={it.position_name}
        meta={formatDateHu(it.created_at)}
      />
    ),
  },
  {
    key: "open_maintenances",
    title: "Nyitott karbantartások",
    icon: Wrench,
    accent: "sky",
    href: "/maintenances",
    cta: "Karbantartások megnyitása",
    renderItem: (it) => (
      <ItemRow
        primary={it.title || it.issue || "Karbantartás"}
        secondary={it.site_name || it.company_name}
        meta={formatDateHu(it.created_at)}
      />
    ),
  },
];

const ACCENT_TOKENS = {
  indigo: {
    bg: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
    ring: "ring-indigo-200/60 dark:ring-indigo-500/20",
    grad: "from-indigo-500 to-indigo-700",
  },
  emerald: {
    bg: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/20",
    grad: "from-emerald-500 to-emerald-700",
  },
  amber: {
    bg: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
    ring: "ring-amber-200/60 dark:ring-amber-500/20",
    grad: "from-amber-500 to-amber-700",
  },
  violet: {
    bg: "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
    ring: "ring-violet-200/60 dark:ring-violet-500/20",
    grad: "from-violet-500 to-violet-700",
  },
  rose: {
    bg: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
    ring: "ring-rose-200/60 dark:ring-rose-500/20",
    grad: "from-rose-500 to-rose-700",
  },
  sky: {
    bg: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300",
    ring: "ring-sky-200/60 dark:ring-sky-500/20",
    grad: "from-sky-500 to-sky-700",
  },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/overview");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e) || "Adatok betöltése sikertelen.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = data
    ? METRICS.reduce((s, m) => s + (data[m.key]?.count || 0), 0)
    : 0;

  return (
    <div className="space-y-7" data-testid="dashboard-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white px-7 py-6 shadow-[0_8px_30px_-20px_rgba(15,23,42,0.14)] dark:border-border dark:from-card dark:to-card">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:18px_18px]"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              Áttekintés
            </p>
            <h1 className="mt-1 text-3xl font-light tracking-tight">
              Szia, <span className="font-medium">{user?.name || user?.email || "admin"}</span>!
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {total > 0
                ? `${total} elem vár figyelmedre — kezdjük a legfontosabbakkal.`
                : "Nincs sürgős feladat — szuper munka!"}
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium shadow-sm dark:border-border dark:bg-card">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
            <span className="tabular-nums">{total} aktív elem</span>
          </div>
        </div>
      </div>

      {/* Bento grid */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-64 animate-pulse rounded-2xl border border-border bg-muted/40"
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {METRICS.map((m) => (
            <MetricCard key={m.key} meta={m} data={data?.[m.key] || { count: 0, items: [] }} />
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ meta, data }) {
  const Icon = meta.icon;
  const tone = ACCENT_TOKENS[meta.accent] || ACCENT_TOKENS.indigo;
  const items = data.items || [];

  return (
    <Link
      to={meta.href}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 transition-all hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-[0_12px_36px_-18px_rgba(15,23,42,0.25)] dark:border-border dark:bg-card"
      data-testid={`metric-${meta.key}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-white shadow-[0_6px_18px_-8px_rgba(15,23,42,0.4)]",
            tone.grad,
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-semibold leading-tight tracking-tight text-slate-700 dark:text-foreground/85">
            {meta.title}
          </p>
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
      </div>

      {/* Big count */}
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-mono text-[44px] font-light leading-none tracking-tight tabular-nums">
          {data.count}
        </span>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
          db
        </span>
      </div>

      {/* Items list (top 3) */}
      <div className="mt-4 flex-1 space-y-1.5 border-t border-slate-100 pt-3 dark:border-border/60">
        {items.length === 0 ? (
          <p className="py-2 text-center text-[11.5px] italic text-slate-400">
            Nincs aktuális elem.
          </p>
        ) : (
          items.slice(0, 3).map((it, i) => (
            <div key={it._id || i}>{meta.renderItem(it)}</div>
          ))
        )}
        {items.length > 3 && (
          <p className="pt-1 text-[10.5px] font-medium text-slate-400">
            +{items.length - 3} további
          </p>
        )}
      </div>

      {/* CTA */}
      <div
        className={cn(
          "mt-4 inline-flex items-center gap-1 self-start rounded-full px-2.5 py-1 text-[10.5px] font-semibold transition-colors",
          tone.bg,
        )}
      >
        {meta.cta}
        <ArrowUpRight className="h-3 w-3" />
      </div>
    </Link>
  );
}

function ItemRow({ primary, secondary, meta }) {
  return (
    <div className="flex items-center gap-2 text-[11.5px]">
      <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" aria-hidden />
      <span className="truncate font-medium text-slate-800 dark:text-foreground">
        {primary || "—"}
      </span>
      {secondary && (
        <span className="truncate text-slate-500 dark:text-muted-foreground">
          · {secondary}
        </span>
      )}
      {meta && (
        <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-slate-400">
          {meta}
        </span>
      )}
    </div>
  );
}
